import React, { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface JsonViewerProps {
  data: unknown
  label?: string
}

const syntaxHighlight = (json: string): string => {
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let cls = 'text-purple-600' // number
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'text-indigo-700 font-medium' // key
        } else {
          cls = 'text-green-700' // string
        }
      } else if (/true|false/.test(match)) {
        cls = 'text-amber-600 font-medium' // boolean
      } else if (/null/.test(match)) {
        cls = 'text-gray-400' // null
      }
      return `<span class="${cls}">${match}</span>`
    }
  )
}

const JsonViewer: React.FC<JsonViewerProps> = ({ data, label }) => {
  const [expanded, setExpanded] = useState(true)

  if (data === null || data === undefined) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        {label && <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>}
        <span className="text-sm text-gray-400 italic">null</span>
      </div>
    )
  }

  const formatted = JSON.stringify(data, null, 2)
  const highlighted = syntaxHighlight(formatted)

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
      <button
        className="flex items-center gap-2 px-4 py-2.5 w-full text-left bg-gray-100 hover:bg-gray-200 transition-colors border-b border-gray-200"
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
        <span className="text-xs font-medium text-gray-600">{label ?? 'JSON'}</span>
      </button>
      {expanded && (
        <pre
          className="px-4 py-3 text-xs font-mono overflow-x-auto leading-relaxed text-gray-800"
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      )}
    </div>
  )
}

export default JsonViewer
